// aiAgentsSlice.js — the current workspace's AI agents (AgentModel), kept in Redux
// the same way lifecycleStageSlice.js keeps Lifecycle stages and inboxSettingsSlice
// keeps teamMembers/teams: a shared array any screen can read (Inbox AI Takeover
// assignment, the assignee display on inbox cards, etc.) instead of each screen
// fetching/holding its own local copy the way AiSettings.jsx's Agents tab still does.
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

// Same client-side dedup pattern as lifecycleStageSlice.js's createCachedThunk —
// re-implemented locally since it isn't exported from that file.
const requestCache = new Map();
const CACHE_DURATION = 10000;

const createCachedThunk = (key, asyncThunk) => {
  return (...args) => (dispatch) => {
    const force = args[0]?.force === true;
    const now = Date.now();
    const cached = requestCache.get(key);
    if (!force && cached && now - cached.timestamp < CACHE_DURATION) {
      return Promise.resolve(cached.result);
    }
    const promise = dispatch(asyncThunk(...args));
    requestCache.set(key, { timestamp: now, result: promise });
    return promise;
  };
};

export const __resetAiAgentsCache = () => {
  requestCache.clear();
};

// GET /api/workspaces/:workspaceId/ai/agents — the same endpoint AiSettings.jsx's
// Agents tab already calls (see aiAgentController.listAgents); AI agents are
// workspace-scoped (AgentModel.workspaceId), so workspaceId is required, unlike
// the account-wide teamMembers/teams thunk in inboxSettingsSlice.
const fetchAiAgentsRaw = createAsyncThunk(
  "aiAgents/fetchAgents",
  async ({ workspaceId } = {}, { rejectWithValue }) => {
    if (!workspaceId) return rejectWithValue("No workspace selected");
    try {
      const token = await getAuthToken();
      const res = await axios.get(`${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/agents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return {
        workspaceId,
        agents: Array.isArray(res.data?.agents) ? res.data.agents : [],
        // { [agentId]: count } — see aiAgentController.listAgents. Upfront, survives a
        // refresh, unlike inboxRecent.agentByAgentId (session-only, populated on click).
        counts: res.data?.aiTakeoverCounts && typeof res.data.aiTakeoverCounts === "object"
          ? res.data.aiTakeoverCounts
          : {},
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchAiAgents = createCachedThunk("fetchAiAgents", fetchAiAgentsRaw);

const initialState = {
  agents: [],
  // { [agentId]: count } — how many contacts are currently under each agent's AI
  // Takeover. Fetched upfront alongside `agents` (see aiAgentController.listAgents),
  // then kept live via adjustAgentCount (dispatched from socketInboxUpdater.js /
  // socketMessageDispatcher.js on an "assign to AI" / "take back from AI" event) —
  // same two-source split inboxSettingsSlice's teamMembers[i].assignedCount uses
  // (fetched upfront, adjusted live), so the Inbox rail's count survives a page
  // refresh instead of only ever existing in the session-only inboxRecent.agentByAgentId.
  counts: {},
  // Which workspace `agents` currently reflects — lets a consumer tell a still-loading/
  // just-switched-workspace apart from "this workspace genuinely has zero agents".
  workspaceId: null,
  loading: false,
  error: null,
};

const aiAgentsSlice = createSlice({
  name: "aiAgents",
  initialState,
  reducers: {
    clearAiAgents(state) {
      state.agents = [];
      state.counts = {};
      state.workspaceId = null;
      state.error = null;
    },
    // Live delta for one agent's count — mirrors inboxSettingsSlice's
    // updateAssignmentCount, just keyed by agent id instead of team/team-member id.
    // Clamped at 0 (a stale/out-of-order decrement should never show negative).
    adjustAgentCount(state, action) {
      const { agentId, delta } = action.payload || {};
      if (!agentId || !delta) return;
      const key = String(agentId);
      const current = state.counts[key] || 0;
      state.counts[key] = Math.max(0, current + delta);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAiAgentsRaw.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAiAgentsRaw.fulfilled, (state, action) => {
        state.loading = false;
        state.agents = action.payload.agents;
        state.counts = action.payload.counts;
        state.workspaceId = action.payload.workspaceId;
      })
      .addCase(fetchAiAgentsRaw.rejected, (state, action) => {
        state.loading = false;
        if (action.payload !== "Debounced") {
          state.error = action.payload;
        }
      });
  },
});

export const { clearAiAgents, adjustAgentCount } = aiAgentsSlice.actions;
export default aiAgentsSlice.reducer;
